export const SECUREMAC_PRO_HERO_STRAPLINE = "SecureShield Pro by LightningFlow";

export const SECUREMAC_PRO_CUSTOMER_PROMISE = Object.freeze({
  headline: "See what's protecting your Mac. And what's connecting.",
  description:
    "SecureShield Pro checks the fourteen security settings that matter and fixes them in a click. It puts Camera, Microphone and Screen Recording a tap away. And with Network Guard, it shows you which apps are reaching out — and lets you decide what's allowed, with your own rules.",
});

/** What SecureShield is, then what Pro adds: the page leads with these three. */
export const SECUREMAC_PRO_PILLARS = Object.freeze([
  Object.freeze({
    id: "checks",
    title: "Security checks",
    summary: "Fourteen of the settings that matter most, read straight from macOS — and fixed in a click.",
    href: "#checks",
  }),
  Object.freeze({
    id: "privacy",
    title: "Privacy controls",
    summary: "Camera, Microphone and Screen Recording, one tap away. You decide which apps keep access.",
    href: "#privacy-controls",
  }),
  Object.freeze({
    id: "network-guard",
    title: "Network Guard",
    summary: "See which apps are connecting, and where. Allow, block or ask — you decide.",
    href: "#network-guard",
    badge: "Pro",
  }),
]);

/** Secure My Mac sweeps only Firewall, Stealth Mode and Automatic Security Updates, with undo. */
export const SECUREMAC_PRO_SECURE_MY_MAC = Object.freeze({
  title: "Secure My Mac.",
  body: "One click turns on your Firewall, Stealth Mode and Automatic Security Updates — and one click undoes it. Settings that could affect something you rely on, like Remote Login, stay as their own one-click switches.",
});

/**
 * SecureShield Pro is SecureShield + Network Guard. Sell what Network Guard does for
 * the customer; never talk about how or where Pro is distributed.
 *
 * Public build 23 has allow/block/ask rules, the Ask prompt,
 * code-signature app identity and seven-day on-device activity.
 * Smart App Profiles and the in-app updater remain in later preview builds.
 * Preserve the current product's
 * default allow when no rule matches, and fail-open when unavailable.
 */
export const SECUREMAC_PRO_NETWORK_GUARD = Object.freeze({
  status: "Included with SecureShield Pro",
  eyebrow: "Network Guard",
  equation: "SecureShield + Network Guard.",
  headline: "A quiet watch over every connection.",
  description:
    "Network Guard shows you which apps on your Mac are reaching out, and where — and lets you decide what's allowed. Set your own rules for the apps you choose.",
  why_pro:
    "Every check you know from SecureShield. Plus Network Guard: see which apps are connecting and decide what's allowed.",
  features: Object.freeze([
    Object.freeze({
      title: "Ask first",
      body: "Choose the destinations that need your say. When an app tries to reach one, you see the app and where it's going — then allow or block, once or always.",
    }),
    Object.freeze({
      title: "Your rules",
      body: "Allow, block or ask by website, domain or IP address. Switch any rule on or off with a click.",
    }),
    Object.freeze({
      title: "Recent Activity",
      body: "A private record of what connected and what was blocked. Kept on your Mac for seven days, and cleared whenever you like.",
    }),
    Object.freeze({
      title: "Knows the real app",
      body: "Apps are recognised by their code signature, not just their name — so a rule follows the real app, and anything unverified is labelled as such.",
    }),
    Object.freeze({
      title: "Connections, not content",
      body: "It sees that an app is connecting — never the message, never the page, never the password.",
    }),
    Object.freeze({
      title: "Quiet until you need it",
      body: "Nothing is blocked until you say so. And if Network Guard ever pauses, your Mac stays online and SecureShield tells you.",
    }),
  ]),
});

/**
 * Account downloads remain available with the customer's purchase.
 */
export const SECUREMAC_PRO_UPDATES = Object.freeze({
  eyebrow: "Updates",
  headline: "Your download. In your account.",
  description:
    "Download SecureShield Pro from your LightningFlow account whenever you need to install or update it.",
  features: Object.freeze([
    Object.freeze({
      title: "Find your download",
      body: "Sign in to your LightningFlow account to see the download available with your purchase.",
    }),
    Object.freeze({
      title: "Choose when to update",
      body: "Download an available release when you are ready to install it.",
    }),
  ]),
});

export const SECUREMAC_PRO_EDITION_COMPARE = Object.freeze([
  Object.freeze({
    label: "SecureShield",
    tagline: "Check and fix",
    checks: "Twelve essential security checks",
    fixes: "Guided, step by step",
    privacy: "Camera, Microphone, Screen Recording",
    network_guard: "Not included",
    updates: "Updated with your Mac",
  }),
  Object.freeze({
    label: "SecureShield Pro",
    tagline: "Check, fix and guard",
    checks: "Fourteen security checks",
    fixes: "One click, plus Secure My Mac",
    privacy: "Camera, Microphone, Screen Recording",
    network_guard: "Included",
    updates: "Download from your account",
  }),
]);

/** Closing section on the SecureShield page, inviting people to look at Pro. */
export const SECUREMAC_PRO_TEASER = Object.freeze({
  eyebrow: "SecureShield Pro",
  headline: "Now see what's connecting.",
  body: "SecureShield Pro is everything on this page — plus Network Guard, so you can see which apps connect and decide what’s allowed.",
  highlights: Object.freeze([
    Object.freeze({ title: "Ask first", body: "Choose the destinations that need your say. Allow or block, once or always." }),
    Object.freeze({ title: "Your rules", body: "Allow, block or ask by website, domain or IP address." }),
    Object.freeze({ title: "Secure My Mac", body: "One click turns on your Firewall, Stealth Mode and Automatic Security Updates." }),
  ]),
});
